import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import { useNavigate, useLocation } from 'react-router-dom';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

/**
 * หน้าแจ้งข้อขัดข้องสำเร็จ (จาก ReporterCreatePage)
 * แสดงเมื่อสร้าง ticket สำเร็จ และส่ง state.ticketData มาจาก ReporterCreatePage
 */
const SendNotification = ({ sx } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const ticketData = location?.state?.ticketData;
  const [zoomImage, setZoomImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!ticketData) navigate('/', { replace: true });
  }, [ticketData, navigate]);

  const handlePrimary = () => navigate('/');

  if (!ticketData) return null;

  const {
    title,
    description,
    location: locationVal,
    address,
    telephone,
    reporterName,
    attachments: attachmentsRaw = [],
    attachmentNames: attachmentNamesLegacy,
  } = ticketData;
  const attachments = attachmentsRaw.length > 0
    ? attachmentsRaw
    : (attachmentNamesLegacy || []).map((name) => ({ name, dataUrl: '' }));

  const inlineRowSx = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 0.75,
    flexWrap: 'wrap',
    py: 0.5,
    borderBottom: '1px solid',
    borderColor: 'divider',
    '&:last-of-type': { borderBottom: 'none' },
  };
  const labelSx = { fontWeight: 600, color: 'text.secondary', minWidth: 72, flexShrink: 0, fontSize: '0.8125rem' };
  const valueSx = { color: 'text.primary', fontSize: '0.8125rem' };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        p: 2,
        ...sx,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.4)',
          animation: 'slideUp 0.4s ease-out',
          '@keyframes slideUp': {
            from: { transform: 'translateY(100%)', opacity: 0 },
            to: { transform: 'translateY(0)', opacity: 1 },
          },
        }}
      >
        <Box
          sx={{
            borderTop: '4px solid',
            borderColor: 'transparent',
            background: 'linear-gradient(to left, rgb(48, 176, 235), rgba(132, 94, 194, 0.8))',
            px: 2,
            pt: 1.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Box
              sx={{
                background: 'rgba(255,255,255,0.25)',
                color: 'white',
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SupportAgentIcon sx={{ fontSize: 24 }} />
            </Box>
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ flex: 1, lineHeight: 1.3 }}>
              มีการแจ้งเหตุเข้ามา
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Box sx={inlineRowSx}>
              <Typography component="span" variant="body2" sx={labelSx}>เรื่อง</Typography>
              <Typography component="span" variant="body2" sx={valueSx}>{title || '-'}</Typography>
            </Box>
            <Box sx={inlineRowSx}>
              <Typography component="span" variant="body2" sx={labelSx}>สถานที่</Typography>
              <Typography component="span" variant="body2" sx={valueSx}>{locationVal || '-'}</Typography>
            </Box>
            <Box sx={inlineRowSx}>
              <Typography component="span" variant="body2" sx={labelSx}>ที่อยู่ผู้แจ้ง</Typography>
              <Typography component="span" variant="body2" sx={valueSx}>{address || '-'}</Typography>
            </Box>
            <Box sx={inlineRowSx}>
              <Typography component="span" variant="body2" sx={labelSx}>ผู้แจ้ง</Typography>
              <Typography component="span" variant="body2" sx={valueSx}>{reporterName || '-'}</Typography>
            </Box>
            <Box sx={inlineRowSx}>
              <Typography component="span" variant="body2" sx={labelSx}>โทร</Typography>
              <Typography component="span" variant="body2" sx={valueSx}>{telephone || '-'}</Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography component="span" variant="body2" sx={{ ...labelSx, display: 'block', mb: 0.5 }}>รายละเอียด</Typography>
            <Typography variant="body2" sx={{ ...valueSx, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{description || '-'}</Typography>
          </Box>

          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: 'rgba(132, 94, 194, 0.12)',
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ImageIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography component="span" variant="body2" sx={{ ...labelSx, display: 'block', mb: 0 }}>รูปแนบ</Typography>
            </Box>
            {attachments.length === 0 ? (
              <Typography variant="body2" sx={{ ...valueSx, color: 'text.disabled', fontStyle: 'italic' }}>ไม่มีรูปแนบ</Typography>
            ) : attachments.length === 1 ? (
              <Box
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: 220,
                }}
              >
                {attachments[0].dataUrl ? (
                  <Box
                    component="img"
                    src={attachments[0].dataUrl}
                    alt={attachments[0].name || 'รูปแนบ'}
                    title={attachments[0].name || 'รูปแนบ'}
                    onClick={() => setZoomImage({ dataUrl: attachments[0].dataUrl, name: attachments[0].name || 'รูปแนบ' })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setZoomImage({ dataUrl: attachments[0].dataUrl, name: attachments[0].name || 'รูปแนบ' }); }}
                    sx={{
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: 220,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      mx: 'auto',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.92 },
                    }}
                  />
                ) : (
                  <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ImageIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: 220,
                  }}
                >
                  {(() => {
                    const main = attachments[selectedIndex];
                    const mainName = main?.name || `รูปที่ ${selectedIndex + 1}`;
                    const mainUrl = main?.dataUrl;
                    return mainUrl ? (
                      <Box
                        component="img"
                        src={mainUrl}
                        alt={mainName}
                        title="กดเพื่อขยาย"
                        onClick={() => setZoomImage({ dataUrl: mainUrl, name: mainName })}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setZoomImage({ dataUrl: mainUrl, name: mainName }); }}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          cursor: 'pointer',
                          display: 'block',
                          '&:hover': { opacity: 0.92 },
                        }}
                      />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      </Box>
                    );
                  })()}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    maxHeight: 220,
                    overflowY: 'auto',
                    flexShrink: 0,
                  }}
                >
                  {attachments.map((att, i) => {
                    const displayName = att.name || `รูปที่ ${i + 1}`;
                    const hasData = Boolean(att.dataUrl);
                    const isSelected = i === selectedIndex;
                    return (
                      <Box
                        key={i}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedIndex(i)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedIndex(i); }}
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          bgcolor: 'grey.100',
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          flexShrink: 0,
                          cursor: 'pointer',
                          transition: 'border-color 0.2s, transform 0.2s',
                          '&:hover': {
                            borderColor: isSelected ? 'primary.main' : 'primary.light',
                            transform: 'scale(1.03)',
                          },
                        }}
                      >
                        {hasData ? (
                          <Box
                            component="img"
                            src={att.dataUrl}
                            alt={displayName}
                            title={displayName}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              pointerEvents: 'none',
                            }}
                          />
                        ) : (
                          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ImageIcon sx={{ fontSize: 24, color: 'text.disabled' }} />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Dialog
          open={Boolean(zoomImage)}
          onClose={() => setZoomImage(null)}
          maxWidth={false}
          PaperProps={{
            sx: {
              maxWidth: '95vw',
              maxHeight: '95vh',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'rgba(0,0,0,0.9)',
            },
          }}
          BackdropProps={{
            sx: { bgcolor: 'rgba(0,0,0,0.8)' },
          }}
        >
          {zoomImage && (
            <>
              <IconButton
                onClick={() => setZoomImage(null)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
                size="small"
                aria-label="ปิด"
              >
                <CloseIcon />
              </IconButton>
              <Box
                component="img"
                src={zoomImage.dataUrl}
                alt={zoomImage.name}
                sx={{
                  maxWidth: '95vw',
                  maxHeight: '95vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </>
          )}
        </Dialog>
      </Paper>
    </Box>
  );
};

export default SendNotification;
